/**
 * Templates page - pick a vertical template and apply it.
 *
 * For sales demos: load Restaurant before walking into a restaurant, Spa
 * before a spa, etc. Applying wipes the six config tables and reseeds
 * from the chosen template. Existing contacts / conversations / messages
 * are untouched.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { useToast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Home,
  Utensils,
  Sparkles,
  Smile,
  Coffee,
  Car,
  Loader2,
  Check,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";

interface TemplateMeta {
  slug: string;
  name: string;
  description: string;
  iconName: string;
  knowledge_doc_count: number;
  qualification_field_count: number;
}

// Maps the icon name the backend ships with each template to the actual
// lucide-react component. Defaults to Sparkles for unknown names.
const ICONS: Record<string, LucideIcon> = {
  Home,
  Utensils,
  Sparkles,
  Smile,
  Coffee,
  Car,
};

export function TemplatesPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateMeta[]>("/api/templates"),
  });

  const { data: business } = useQuery({
    queryKey: ["settings", "business"],
    queryFn: () =>
      api.get<{ current_template_slug: string | null; business_name: string }>(
        "/api/settings/business",
      ),
  });

  const activeSlug = business?.current_template_slug ?? null;

  const [confirming, setConfirming] = useState<TemplateMeta | null>(null);

  const applyMut = useMutation({
    mutationFn: (slug: string) =>
      api.post<{ slug: string; name: string }>("/api/templates/apply", { slug }),
    onSuccess: (res) => {
      // Invalidate everything that depends on the templated config.
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success(`Switched to ${res.name}`);
      setConfirming(null);
    },
    onError: (e) =>
      toast.error("Apply failed", e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">
            Vertical templates
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            One click to re-skin the bot for a different kind of business.
            Use it to demo to a restaurant, then a spa, then a dentist, in
            the same day.
          </p>
        </div>
        {activeSlug && (
          <Badge variant="info" className="!py-1 !px-2 text-xs">
            Active: {templates.find((t) => t.slug === activeSlug)?.name ?? activeSlug}
          </Badge>
        )}
      </div>

      <Card className="px-4 py-3 bg-amber-50 border-amber-200 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900">
          Applying a template <strong>replaces</strong> the business identity,
          persona, qualification fields, scoring rules, takeover rules, and
          the entire knowledge base. It does not touch contacts, conversations,
          or messages.
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-12 text-center text-sm text-slate-400">
          Loading templates…
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => {
            const Icon = ICONS[t.iconName] ?? Sparkles;
            const isActive = activeSlug === t.slug;
            return (
              <Card
                key={t.slug}
                className={cn(
                  "p-4 flex flex-col gap-3 border-2",
                  isActive
                    ? "border-emerald-400 bg-emerald-50/30"
                    : "border-transparent",
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      {t.name}
                      {isActive && (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      {t.slug}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {t.description}
                </p>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 tabular-nums">
                  <span>{t.knowledge_doc_count} knowledge docs</span>
                  <span>{t.qualification_field_count} fields</span>
                </div>
                <div className="mt-auto pt-2">
                  <Button
                    size="sm"
                    variant={isActive ? "outline" : "primary"}
                    className="w-full"
                    disabled={isActive || applyMut.isPending}
                    onClick={() => setConfirming(t)}
                  >
                    {isActive ? "Currently active" : "Apply"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-5 space-y-3">
            <div className="text-sm font-semibold text-slate-900">
              Switch to {confirming.name}?
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              This replaces the bot's identity, persona, qualification fields,
              scoring rules, takeover rules and knowledge base with the{" "}
              <strong>{confirming.name}</strong> template. Existing contacts
              and conversations stay untouched but the bot will respond as a
              different business going forward.
            </p>
            <div className="flex items-center gap-2 justify-end pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirming(null)}
                disabled={applyMut.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => applyMut.mutate(confirming.slug)}
                disabled={applyMut.isPending}
              >
                {applyMut.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Applying…
                  </>
                ) : (
                  `Apply ${confirming.name}`
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
